import { useEffect, useRef, useState } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Bot, Copy, Mail, CheckCircle2, AlertTriangle, ExternalLink, Eye, EyeOff, Users, CheckCircle, XCircle, RefreshCw, Building2, DoorOpen, ImagePlus, UserSquare2 } from "lucide-react";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";
import { isDemoMode } from "@/components/DemoBanner";
import { formatDistanceToNow } from "date-fns";
import type { RoomsSyncProgress } from "@shared/padsplit-rooms-sync-progress";

type HarvestType = "properties" | "rooms" | "members";

const HARVEST_LABELS: Record<HarvestType, string> = {
  properties: "properties",
  rooms: "rooms",
  members: "members",
};

// Per-type "request accepted" toasts. These fire when the harvest EVENT is
// queued, never when data has landed — the wording must not claim completion.
// Rooms gets its own copy because its pipeline is two-stage: the list fetch
// completes first (harvest_rooms_completed), then per-room detail jobs keep
// persisting in the background.
export const HARVEST_STARTED_TOASTS: Record<HarvestType, { title: string; description: string }> = {
  properties: {
    title: "Sync started",
    description: "Your properties will update in the background — usually within a few minutes.",
  },
  rooms: {
    title: "Room sync started",
    description:
      "The room list is being fetched now, and each room's details will continue processing in the background. Progress appears below.",
  },
  members: {
    title: "Sync started",
    description: "Your members will update in the background — usually within a few minutes.",
  },
};

export type OwnerRoomsProgress = RoomsSyncProgress & { ownerId: number };

const ROOMS_PROCESSING_PHASES = ["fetching_list", "processing_rooms"] as const;

export function isRoomsProcessingPhase(phase: RoomsSyncProgress["phase"]): boolean {
  return (ROOMS_PROCESSING_PHASES as readonly string[]).includes(phase);
}

// With multiple accessible owners, surface the entry that most needs the
// user's attention: an in-flight sync first, then failures, then success.
const ROOMS_PHASE_PRIORITY: RoomsSyncProgress["phase"][] = [
  "processing_rooms",
  "fetching_list",
  "partial_failure",
  "error",
  "success",
];

export function pickRoomsProgress(
  entries: OwnerRoomsProgress[] | undefined,
): OwnerRoomsProgress | null {
  if (!entries || entries.length === 0) return null;
  for (const phase of ROOMS_PHASE_PRIORITY) {
    const match = entries.find((e) => e.phase === phase);
    if (match) return match;
  }
  return entries[0];
}

// Terminal rooms outcomes stay visible for this long after the list fetch,
// then the row disappears (an old success/failure isn't news forever).
export const ROOMS_PROGRESS_DISPLAY_WINDOW_MS = 24 * 60 * 60 * 1000;

export function shouldShowRoomsProgress(
  progress: RoomsSyncProgress | null,
  now: Date = new Date(),
): boolean {
  if (!progress) return false;
  if (isRoomsProcessingPhase(progress.phase)) return true;
  if (!progress.listCompletedAt) return false;
  return now.getTime() - new Date(progress.listCompletedAt).getTime() <= ROOMS_PROGRESS_DISPLAY_WINDOW_MS;
}

export function describeRoomsProgress(p: RoomsSyncProgress): string {
  switch (p.phase) {
    case "fetching_list":
      return "Fetching the room list from PadSplit…";
    case "processing_rooms":
      return p.expectedRooms != null
        ? `Processing room details — ${p.processedRooms} of ${p.expectedRooms} rooms synced`
        : "Room details are processing in the background…";
    case "success":
      return p.expectedRooms != null && p.expectedRooms > 0
        ? `Room sync complete — ${p.expectedRooms} rooms processed`
        : `Room sync complete${p.message ? ` — ${p.message}` : ""}`;
    case "partial_failure":
      return p.expectedRooms != null
        ? `Room sync partially complete — ${p.processedRooms} of ${p.expectedRooms} rooms synced, ${p.failedRooms} did not finish`
        : "Room sync partially complete";
    case "error":
      return `Room sync failed${p.message ? ` — ${p.message}` : ""}`;
  }
}

export function RoomsProgressRow({ progress, now }: { progress: RoomsSyncProgress | null; now?: Date }) {
  if (!progress || !shouldShowRoomsProgress(progress, now)) return null;
  const text = describeRoomsProgress(progress);

  if (isRoomsProcessingPhase(progress.phase)) {
    return (
      <div
        className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm"
        data-testid="rooms-progress-row"
      >
        <RefreshCw className="h-4 w-4 shrink-0 animate-spin text-blue-600" />
        <span className="font-medium text-blue-900">{text}</span>
      </div>
    );
  }
  if (progress.phase === "success") {
    return (
      <div
        className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm"
        data-testid="rooms-progress-row"
      >
        <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
        <span className="font-medium text-green-900">{text}</span>
        {progress.skippedNoId > 0 && (
          <span className="text-green-800 text-xs">
            {progress.skippedNoId} room{progress.skippedNoId === 1 ? "" : "s"} without a PadSplit id skipped
          </span>
        )}
      </div>
    );
  }
  if (progress.phase === "partial_failure") {
    return (
      <div
        className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm"
        data-testid="rooms-progress-row"
      >
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
        <span className="font-medium text-amber-900">{text}</span>
      </div>
    );
  }
  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm"
      data-testid="rooms-progress-row"
    >
      <XCircle className="h-4 w-4 shrink-0 text-red-600" />
      <span className="font-medium text-red-900">{text}</span>
    </div>
  );
}

interface AssistantStatus {
  isTeamMember: boolean;
  claimed: boolean;
  assistantName: string | null;
  assistantEmail: string | null;
  teamRole: "owner" | "admin" | "member" | "viewer" | null;
}

interface ClaimResponse {
  assistantName: string;
  assistantEmail: string;
}

export interface SyncState {
  id: number;
  userId: number;
  syncType: string;
  status: string;
  lastRunAt: string | null;
  lastSyncedAt: string | null;
  lastError: string | null;
  errorMessage: string | null;
}

type AggSyncStatus = "active" | "failing" | "pending" | "stale";

// A feed that once synced successfully but hasn't succeeded in this long is
// considered stale — the external sync workflows normally run at least daily,
// so 48h of silence means the feed has stalled (e.g. the member_messages
// workflow outage) and the page must not keep showing a green "Active".
export const SYNC_STALE_AFTER_MS = 48 * 60 * 60 * 1000;

// Human labels for the externally-written sync_states feed names.
const SYNC_TYPE_LABELS: Record<string, string> = {
  properties: "Properties",
  rooms: "Rooms",
  members: "Members",
  member_messages: "Messages",
  admins: "Admins",
  financials: "Financials",
  tasks: "Tasks",
  locks: "Lock codes",
};

export function syncTypeLabel(syncType: string): string {
  return SYNC_TYPE_LABELS[syncType] ?? syncType.replace(/_/g, " ");
}

export interface StaleFeed {
  syncType: string;
  lastSyncedAt: Date;
}

export function deriveSyncStatus(
  states: SyncState[],
  now: Date = new Date(),
): { status: AggSyncStatus; latestSyncedAt: Date | null; staleFeeds: StaleFeed[] } {
  if (!states || states.length === 0) return { status: "pending", latestSyncedAt: null, staleFeeds: [] };

  const attempted = states.filter((s) => s.lastRunAt !== null);
  if (attempted.length === 0) return { status: "pending", latestSyncedAt: null, staleFeeds: [] };

  const latestSyncedAt = attempted.reduce<Date | null>((best, s) => {
    if (!s.lastSyncedAt) return best;
    const d = new Date(s.lastSyncedAt);
    return best === null || d > best ? d : best;
  }, null);

  // No confirmed successful sync yet — still pending regardless of whether
  // runs have been attempted.
  if (latestSyncedAt === null) return { status: "pending", latestSyncedAt: null, staleFeeds: [] };

  const allFailing = attempted.every((s) => {
    const hasError = s.lastError !== null || s.status === "failed";
    if (!hasError) return false;
    if (!s.lastSyncedAt) return true;
    const lastErrorRun = s.lastRunAt ? new Date(s.lastRunAt) : null;
    const lastOk = new Date(s.lastSyncedAt);
    return lastErrorRun !== null && lastErrorRun > lastOk;
  });

  // Per-feed staleness: a feed that HAS synced successfully before but whose
  // newest success is older than the threshold has silently stalled (the
  // external writer stopped updating it). Surface those feeds explicitly.
  const staleFeeds: StaleFeed[] = attempted
    .filter((s) => s.lastSyncedAt !== null)
    .map((s) => ({ syncType: s.syncType, lastSyncedAt: new Date(s.lastSyncedAt as string) }))
    .filter((f) => now.getTime() - f.lastSyncedAt.getTime() > SYNC_STALE_AFTER_MS)
    .sort((a, b) => a.lastSyncedAt.getTime() - b.lastSyncedAt.getTime());

  if (allFailing) return { status: "failing", latestSyncedAt, staleFeeds };
  if (staleFeeds.length > 0) return { status: "stale", latestSyncedAt, staleFeeds };
  return { status: "active", latestSyncedAt, staleFeeds };
}

export function SyncStatusRow({ states, isLoading }: { states: SyncState[] | undefined; isLoading: boolean }) {
  if (isLoading) {
    return <div className="h-10 w-full rounded-lg bg-gray-100 animate-pulse" />;
  }
  if (!states) return null;

  const { status, latestSyncedAt, staleFeeds } = deriveSyncStatus(states);
  if (status === "pending") return null;

  if (status === "stale") {
    return (
      <div
        className="flex flex-col gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm"
        data-testid="sync-status-row"
      >
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
          <span className="font-medium text-amber-900">Some data may be out of date</span>
          {latestSyncedAt && (
            <span className="text-amber-800 text-xs">
              Last successful sync {formatDistanceToNow(latestSyncedAt, { addSuffix: true })}
            </span>
          )}
        </div>
        <ul className="ml-7 space-y-0.5">
          {staleFeeds.map((f) => (
            <li key={f.syncType} className="text-xs text-amber-800" data-testid={`text-stale-feed-${f.syncType}`}>
              {syncTypeLabel(f.syncType)} last synced {formatDistanceToNow(f.lastSyncedAt, { addSuffix: true })}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (status === "active") {
    return (
      <div
        className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm"
        data-testid="sync-status-row"
      >
        <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
        <span className="font-medium text-green-900">Active</span>
        {latestSyncedAt && (
          <span className="text-green-800 text-xs">
            Last synced {formatDistanceToNow(latestSyncedAt, { addSuffix: true })}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm"
      data-testid="sync-status-row"
    >
      <XCircle className="h-4 w-4 shrink-0 text-red-600" />
      <span className="font-medium text-red-900">Sync issue detected</span>
      <a
        href="mailto:support@padpulse.io"
        className="ml-auto text-xs text-red-700 underline hover:text-red-900"
        data-testid="link-contact-support"
      >
        Contact Support
      </a>
    </div>
  );
}

function CopyableField({ label, value, masked = false, testId }: { label: string; value: string; masked?: boolean; testId: string }) {
  const { toast } = useToast();
  const [revealed, setRevealed] = useState(!masked);
  const display = masked && !revealed ? "•".repeat(Math.max(8, Math.min(value.length, 16))) : value;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 font-mono text-sm text-gray-900 break-all" data-testid={testId}>{display}</code>
        {masked && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setRevealed((r) => !r)}
            data-testid={`${testId}-toggle`}
          >
            {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            navigator.clipboard.writeText(value).then(
              () => toast({ title: "Copied to clipboard" }),
              () => toast({ title: "Copy failed", variant: "destructive" }),
            );
          }}
          data-testid={`${testId}-copy`}
        >
          <Copy className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

interface AssistantBranding {
  displayName: string | null;
  logoUrl: string | null;
  canEdit: boolean;
}

async function uploadLogoFile(file: File): Promise<string> {
  const reader = new FileReader();
  const dataUrl: string = await new Promise((resolve, reject) => {
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("File read failed"));
    reader.readAsDataURL(file);
  });
  const base64 = dataUrl.split(",")[1];
  // The logo is stored in PUBLIC object storage and returned as a full public
  // https URL (not the private /api/app/images auth-gated path), because it's
  // shown to PadSplit members and fetched by the external branding consumer.
  const res = await apiRequest("POST", "/api/app/padsplit/assistant/logo", {
    filename: file.name,
    mimeType: file.type,
    data: base64,
  });
  const data = await res.json();
  return data.imageUrl as string;
}

function AssistantBrandingCard({ pageReadOnly }: { pageReadOnly: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [displayName, setDisplayName] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const initialized = useRef(false);

  const brandingQuery = useQuery<AssistantBranding>({
    queryKey: ["/api/app/padsplit/assistant/branding"],
  });

  // Seed the controlled form once from the loaded values. Subsequent saves
  // update the cache; we don't re-snap the user's in-progress edits back.
  useEffect(() => {
    if (initialized.current || !brandingQuery.data) return;
    initialized.current = true;
    setDisplayName(brandingQuery.data.displayName ?? "");
    setLogoUrl(brandingQuery.data.logoUrl ?? null);
  }, [brandingQuery.data]);

  const canEdit = !pageReadOnly && (brandingQuery.data?.canEdit ?? false);

  const saveMutation = useMutation<AssistantBranding, Error, void>({
    mutationFn: async () => {
      const res = await apiRequest("PUT", "/api/app/padsplit/assistant/branding", {
        displayName: displayName.trim(),
        logoUrl,
      });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/app/padsplit/assistant/branding"], data);
      queryClient.invalidateQueries({ queryKey: ["/api/app/padsplit/assistant/branding"] });
      toast({ title: "Assistant branding saved", description: "Your display name and logo were updated." });
    },
    onError: (err) => {
      toast({
        title: "Couldn't save assistant branding",
        description: parseErrorMessage(err, "Please try again or contact support."),
        variant: "destructive",
      });
    },
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please choose an image file.", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const url = await uploadLogoFile(file);
      setLogoUrl(url);
    } catch (err) {
      toast({
        title: "Logo upload failed",
        description: parseErrorMessage(err, "Please try again with a different image."),
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card data-testid="card-assistant-branding">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <UserSquare2 className="h-5 w-5 text-brand" />
          Assistant Display Name &amp; Logo
        </CardTitle>
        <CardDescription>
          Control how your PadPulse assistant appears to PadSplit members — set a display name and profile logo instead of the generic first name.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {brandingQuery.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-24 rounded-full" />
          </div>
        ) : (
          <>
            {!canEdit && (
              <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="callout-branding-readonly">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <p>You can view the assistant's display name and logo, but only the account owner or an admin can change them.</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="assistant-display-name">Display name</Label>
              <Input
                id="assistant-display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Property Manager"
                maxLength={80}
                disabled={!canEdit || saveMutation.isPending}
                data-testid="input-assistant-display-name"
              />
              <p className="text-xs text-gray-500">This is the name PadSplit members will see when they chat with your assistant.</p>
            </div>

            <div className="space-y-2">
              <Label>Logo</Label>
              <div className="flex items-center gap-4">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-50">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Assistant logo preview"
                      className="h-full w-full object-cover"
                      data-testid="img-assistant-logo-preview"
                    />
                  ) : (
                    <Bot className="h-8 w-8 text-gray-400" data-testid="img-assistant-logo-placeholder" />
                  )}
                </div>
                {canEdit && (
                  <div className="flex flex-col gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileChange}
                      data-testid="input-assistant-logo-file"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || saveMutation.isPending}
                      data-testid="button-upload-assistant-logo"
                    >
                      {uploading ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ImagePlus className="h-4 w-4 mr-2" />
                      )}
                      {logoUrl ? "Replace logo" : "Upload logo"}
                    </Button>
                    {logoUrl && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-gray-500"
                        onClick={() => setLogoUrl(null)}
                        disabled={uploading || saveMutation.isPending}
                        data-testid="button-remove-assistant-logo"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {canEdit && (
              <div className="flex justify-end">
                <Button
                  className="bg-brand hover:bg-brand-hover text-white"
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending || uploading || displayName.trim().length === 0}
                  data-testid="button-save-assistant-branding"
                >
                  {saveMutation.isPending ? "Saving..." : "Save"}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function AppPadsplitSync() {
  const queryClient = useQueryClient();
  usePageTitle("PadSplit Sync");
  const { canEdit } = usePermissions();
  const readOnly = !canEdit("padsplit_sync");
  const { toast } = useToast();
  const autoClaimAttempted = useRef(false);
  const demo = isDemoMode();

  const statusQuery = useQuery<AssistantStatus>({
    queryKey: ["/api/app/padsplit/assistant"],
    enabled: !demo,
  });

  const syncStatesQuery = useQuery<SyncState[]>({
    queryKey: ["/api/app/padsplit/sync-states"],
    refetchInterval: 60_000,
    enabled: !demo,
  });

  // Honest rooms-harvest progress (the sync_states row can read "success" as
  // soon as the room LIST is fetched, before the per-room detail jobs land).
  // Poll faster while a run is actually processing so the counts move.
  const roomsProgressQuery = useQuery<{ progress: OwnerRoomsProgress[] }>({
    queryKey: ["/api/app/padsplit/rooms-sync-progress"],
    refetchInterval: (query) =>
      query.state.data?.progress?.some((p) => isRoomsProcessingPhase(p.phase)) ? 15_000 : 60_000,
    enabled: !demo,
  });
  const roomsProgress = pickRoomsProgress(roomsProgressQuery.data?.progress);
  // Mirror the server's Members guard: only PARENT-log-derived (non-legacy)
  // processing locks the button. Legacy/child-telemetry progress is
  // display-only — the server would accept the request, so don't disable it.
  const roomsProcessing =
    roomsProgress != null && isRoomsProcessingPhase(roomsProgress.phase) && !roomsProgress.legacy;

  const claimMutation = useMutation<ClaimResponse, Error, void>({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/padsplit/assistant/claim", {});
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/padsplit/assistant"] });
      toast({ title: "Assistant ready", description: `Meet ${data.assistantName} — your PadPulse assistant.` });
    },
    onError: (err: any) => {
      // 409 means another concurrent request (or prior visit) already claimed an assistant.
      // Treat as success: refetch canonical status and don't show an error.
      const status = err?.status ?? err?.response?.status;
      const message = String(err?.message ?? "");
      if (status === 409 || /ASSISTANT_ALREADY_CLAIMED|already claimed/i.test(message)) {
        queryClient.invalidateQueries({ queryKey: ["/api/app/padsplit/assistant"] });
        return;
      }
      toast({
        title: "Could not assign an assistant",
        description: parseErrorMessage(err, "Please try again or contact support."),
        variant: "destructive",
      });
    },
  });

  const [pendingHarvest, setPendingHarvest] = useState<HarvestType | null>(null);
  const harvestMutation = useMutation<unknown, Error, HarvestType>({
    mutationFn: async (type) => {
      const res = await apiRequest("POST", `/api/app/padsplit/harvest/${type}`, {});
      return res.json();
    },
    onMutate: (type) => setPendingHarvest(type),
    onSuccess: (_data, type) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/padsplit/sync-states"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/padsplit/rooms-sync-progress"] });
      toast(HARVEST_STARTED_TOASTS[type]);
    },
    onError: (err, type) => {
      // The server rejects Sync Members with 409 ROOMS_SYNC_IN_PROGRESS while
      // rooms detail jobs are still persisting — that's sequencing, not a
      // failure, so surface it as an informational toast.
      const message = err instanceof Error ? err.message : "";
      if (type === "members" && /ROOMS_SYNC_IN_PROGRESS/.test(message)) {
        toast({
          title: "Rooms are still syncing",
          description: parseErrorMessage(err, "Try again once room processing finishes."),
        });
        return;
      }
      toast({
        title: `Couldn't start ${HARVEST_LABELS[type]} sync`,
        description: parseErrorMessage(err, "Please try again or contact support."),
        variant: "destructive",
      });
    },
    onSettled: () => setPendingHarvest(null),
  });

  // Auto-claim on first visit if user hasn't yet. One-shot guard prevents
  // double-fire under StrictMode / HMR / remounts.
  // Team members are never auto-claimed — they share the owner's assistant.
  useEffect(() => {
    if (demo) return;
    if (autoClaimAttempted.current) return;
    if (!statusQuery.data || readOnly) return;
    if (statusQuery.data.claimed) return;
    if (statusQuery.data.isTeamMember) return;
    autoClaimAttempted.current = true;
    claimMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusQuery.data, readOnly, demo]);

  const status = statusQuery.data;
  const isTeamMember = status?.isTeamMember ?? false;
  const teamRole = status?.teamRole ?? null;
  // Admin team members may trigger a manual sync on the owner's behalf (the
  // backend authorizes by role + runs the harvest under the owner's assistant).
  const isAdminTeamMember = isTeamMember && teamRole === "admin";
  const assistantName = status?.assistantName ?? claimMutation.data?.assistantName ?? null;
  const assistantEmail = status?.assistantEmail ?? claimMutation.data?.assistantEmail ?? null;
  const isClaimed = !!assistantName;
  // The Manual Sync card shows for the account owner OR an admin team member,
  // but only once the owner's assistant is claimed.
  const canManualHarvest = isClaimed && (!isTeamMember || isAdminTeamMember);
  const isLoading = statusQuery.isLoading || claimMutation.isPending;

  if (demo) {
    return (
      <AppLayout>
        <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="page-padsplit-sync">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="text-padsplit-sync-title">PadSplit Sync</h1>
              <p className="text-sm text-gray-500 mt-1">
                Your PadSplit account is connected — members, properties, rooms, and messages sync automatically.
              </p>
            </div>
          </div>

          <Card data-testid="card-demo-sync-status">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Bot className="h-5 w-5 text-brand" />
                PadSplit Connection
              </CardTitle>
              <CardDescription>
                Syncing runs automatically in the background. There's nothing you need to do.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm"
                data-testid="sync-status-row"
              >
                <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
                <span className="font-medium text-green-900">Sync active</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="page-padsplit-sync">
        <ViewOnlyBanner area="padsplit_sync" />
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-padsplit-sync-title" data-tour="padsplit-sync-heading">PadSplit Sync</h1>
            <p className="text-sm text-gray-500 mt-1">
              Your personal PadPulse assistant connects to PadSplit on your behalf to sync members, properties, rooms, and messages automatically.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="padsplit-sync" />
            <GuideButton pageKey="padsplit-sync" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="padsplit-sync" />

        <Card data-testid="card-assistant" data-tour="padsplit-sync-status">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Bot className="h-5 w-5 text-brand" />
              {isTeamMember ? "Owner's PadPulse Assistant" : "Your PadPulse Assistant"}
            </CardTitle>
            <CardDescription>
              {isTeamMember
                ? "Your PadPulse user communicates through the primary owner's PadPulse assistant. All syncing happens under the owner's account."
                : "Each PadPulse account is paired with a unique assistant. Add your assistant to your PadSplit account as a team member so it can sync your data securely."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isTeamMember ? (
              <>
                {isLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : isClaimed && assistantName && assistantEmail ? (
                  <>
                    <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="callout-team-member">
                      <Users className="h-4 w-4 shrink-0 mt-0.5" />
                      <p>You're a team member. Your PadPulse user communicates through the primary owner's PadPulse assistant shown below.</p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <CopyableField
                        label="Owner's assistant name"
                        value={assistantName}
                        testId="text-assistant-name"
                      />
                      <CopyableField
                        label="Owner's assistant email"
                        value={assistantEmail}
                        testId="text-assistant-email"
                      />
                    </div>
                    <SyncStatusRow states={syncStatesQuery.data} isLoading={syncStatesQuery.isLoading} />
                  </>
                ) : (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="callout-owner-no-assistant">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <p>The primary owner hasn't set up their PadPulse assistant yet. Ask your account owner to visit this page to complete setup.</p>
                  </div>
                )}
              </>
            ) : (
              <>
                {isLoading && !isClaimed ? (
                  <div className="space-y-3">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : isClaimed && assistantName && assistantEmail ? (
                  <>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <CopyableField
                        label="Assistant name"
                        value={assistantName}
                        testId="text-assistant-name"
                      />
                      <CopyableField
                        label="Assistant email"
                        value={assistantEmail}
                        testId="text-assistant-email"
                      />
                    </div>
                    <SyncStatusRow states={syncStatesQuery.data} isLoading={syncStatesQuery.isLoading} />
                  </>
                ) : (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p>We couldn't assign an assistant automatically. Try again below.</p>
                      <Button
                        size="sm"
                        className="mt-2 bg-brand hover:bg-brand-hover text-white"
                        onClick={() => claimMutation.mutate()}
                        disabled={readOnly || claimMutation.isPending}
                        data-testid="button-claim-assistant"
                      >
                        {claimMutation.isPending ? "Assigning..." : "Assign my assistant"}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {isClaimed && <AssistantBrandingCard pageReadOnly={readOnly} />}

        {isClaimed && (
          <Card data-testid="card-instructions" data-tour="padsplit-sync-trigger">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="h-5 w-5 text-brand" />
                Add your assistant to PadSplit
              </CardTitle>
              <CardDescription>
                Invite your assistant into your PadSplit account so it can begin syncing on your behalf.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3 text-sm text-gray-700 list-decimal list-inside">
                <li>
                  <a
                    href="https://www.padsplit.com/host/team"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand underline inline-flex items-center gap-1"
                    data-testid="link-padsplit"
                  >
                    Log in to PadSplit team
                    <ExternalLink className="h-3 w-3" />
                  </a>{" "}
                  with your <strong>primary host account</strong>.
                </li>
                <li>Click <strong>Invite</strong> in the top-right corner.</li>
                <li>
                  Enter your assistant's email:{" "}
                  <code className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-900" data-testid="text-instruction-email">
                    {assistantEmail}
                  </code>
                </li>
                <li>
                  Set the role to <strong>Property Admin</strong> and select <strong>all properties</strong>.
                  <div className="mt-2 ml-4 flex items-start gap-2 rounded-md border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="callout-future-properties">
                    <span className="font-bold">*</span>
                    <span>
                      You'll need to add every <strong>future new property</strong> to this assistant under Property Admin permissions, or it won't be able to sync them.
                    </span>
                  </div>
                </li>
                <li>Click <strong>Send invitation</strong>.</li>
                <li className="text-amber-900">
                  <strong>Repeat steps 1–5 a second time.</strong> PadSplit requires a secondary login + verification but doesn't surface that requirement until <em>after</em> you click Invite the first time, so you'll need to send the invitation twice.
                </li>
                <li>PadPulse will detect the invite, accept it automatically, and begin syncing your data — usually within a few minutes.</li>
              </ol>
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-900">
                <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                <p>
                  Once your assistant has access, you can come back here to see sync status. There's nothing else you need to do — sync runs automatically in the background.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {canManualHarvest && (
          <Card data-testid="card-manual-sync">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <RefreshCw className="h-5 w-5 text-brand" />
                Manual Sync
              </CardTitle>
              <CardDescription>
                Sync usually runs automatically. Use these buttons to pull the latest data from PadSplit on demand — each runs in the background and updates within a few minutes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {readOnly && !isAdminTeamMember && (
                <div className="mb-4">
                  <ViewOnlyBanner area="padsplit_sync" />
                </div>
              )}
              {roomsProgress && shouldShowRoomsProgress(roomsProgress) && (
                <div className="mb-4">
                  <RoomsProgressRow progress={roomsProgress} />
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-3">
                <Button
                  variant="outline"
                  className="justify-start"
                  data-testid="button-harvest-properties"
                  disabled={(readOnly && !isAdminTeamMember) || harvestMutation.isPending}
                  onClick={() => harvestMutation.mutate("properties")}
                >
                  {pendingHarvest === "properties" ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Building2 className="h-4 w-4 mr-2" />
                  )}
                  Sync Properties
                </Button>
                <Button
                  variant="outline"
                  className="justify-start"
                  data-testid="button-harvest-rooms"
                  disabled={(readOnly && !isAdminTeamMember) || harvestMutation.isPending}
                  onClick={() => harvestMutation.mutate("rooms")}
                >
                  {pendingHarvest === "rooms" ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <DoorOpen className="h-4 w-4 mr-2" />
                  )}
                  Sync Rooms
                </Button>
                <Button
                  variant="outline"
                  className="justify-start"
                  data-testid="button-harvest-members"
                  disabled={(readOnly && !isAdminTeamMember) || harvestMutation.isPending || roomsProcessing}
                  title={
                    roomsProcessing
                      ? "Rooms are still syncing — Members sync unlocks when room processing finishes."
                      : undefined
                  }
                  onClick={() => harvestMutation.mutate("members")}
                >
                  {pendingHarvest === "members" ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Users className="h-4 w-4 mr-2" />
                  )}
                  Sync Members
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
